import React from 'react';
import Icon from '../../../components/AppIcon';

const OrderSummaryCard = ({ order }) => {
  if (!order) return null;

  const getStatusColor = (status) => {
    const colors = {
      'Cotizada': 'bg-muted text-muted-foreground',
      'Aprobada': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      'En proceso': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
      'Lista': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      'Entregada': 'bg-primary/10 text-primary',
      'Cancelada': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const calculateTotals = () => {
    const subtotal = order?.items?.reduce((sum, item) => sum + item?.total, 0);
    const discount = order?.discount || 0;
    const total = subtotal - discount;
    const paid = order?.payments?.reduce((sum, p) => sum + p?.amount, 0) || 0;
    const balance = total - paid;

    return { subtotal, discount, total, paid, balance };
  };

  const totals = calculateTotals();

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-border">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="FileText" size={20} color="var(--color-primary)" />
            <h3 className="text-base md:text-lg font-semibold text-foreground">
              {order?.folio}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {new Date(order.date)?.toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </p>
        </div>
        <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${getStatusColor(order?.status)}`}>
          {order?.status}
        </span>
      </div>
      <div className="space-y-3 mb-6">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Detalles de la Orden
        </h4>
        <div className="space-y-2">
          {order?.items?.map((item, index) => (
            <div key={index} className="flex justify-between items-start gap-4 py-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-medium text-foreground truncate">
                  {item?.name}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {item?.quantity} × ${item?.price?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <p className="text-sm md:text-base font-semibold text-foreground whitespace-nowrap">
                ${item?.total?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3 pt-4 border-t border-border">
        <div className="flex justify-between text-sm md:text-base">
          <span className="text-muted-foreground">Subtotal:</span>
          <span className="font-medium text-foreground">
            ${totals?.subtotal?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
          </span>
        </div>
        {totals?.discount > 0 && (
          <div className="flex justify-between text-sm md:text-base">
            <span className="text-muted-foreground">Descuento:</span>
            <span className="font-medium text-red-600 dark:text-red-400">
              -${totals?.discount?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
            </span>
          </div>
        )}
        <div className="flex justify-between text-base md:text-lg font-semibold pt-2 border-t border-border">
          <span className="text-foreground">Total:</span>
          <span className="text-primary">
            ${totals?.total?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
          </span>
        </div>
        {totals?.paid > 0 && (
          <>
            <div className="flex justify-between text-sm md:text-base">
              <span className="text-muted-foreground">Pagado:</span>
              <span className="font-medium text-green-600 dark:text-green-400">
                ${totals?.paid?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between text-base md:text-lg font-bold pt-2 border-t border-border">
              <span className="text-foreground">Saldo Pendiente:</span>
              <span className={totals?.balance > 0 ? 'text-warning' : 'text-success'}>
                ${totals?.balance?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSummaryCard;